import React, { Suspense, lazy } from "react";
import { Resource } from "react-admin";
import { HiDatabase } from "react-icons/hi";
import { IoCalendarSharp } from "react-icons/io5";
import { IoAlbumsSharp } from "react-icons/io5";
import Icon from "@material-ui/core/Icon";
import Avatar from "@material-ui/core/Avatar";
import CircularProgress from "@material-ui/core/CircularProgress";

// Loading component for Suspense fallback
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
    <CircularProgress />
  </div>
);

// Helper to wrap a lazy component with Suspense
const withSuspense = (LazyComponent) => (props) => (
  <Suspense fallback={<PageLoader />}>
    <LazyComponent {...props} />
  </Suspense>
);

// Helper to create lazy-loaded resource props
const lazyResource = (importFn) => {
  // Create wrapped components for list, show, edit, create
  const WrappedList = withSuspense(
    lazy(() => importFn().then((mod) => ({ default: mod.default?.list || mod.list || (() => null) })))
  );
  const WrappedShow = withSuspense(
    lazy(() => importFn().then((mod) => ({ default: mod.default?.show || mod.show || (() => null) })))
  );
  const WrappedEdit = withSuspense(
    lazy(() => importFn().then((mod) => ({ default: mod.default?.edit || mod.edit || (() => null) })))
  );
  const WrappedCreate = withSuspense(
    lazy(() => importFn().then((mod) => ({ default: mod.default?.create || mod.create || (() => null) })))
  );

  return {
    list: WrappedList,
    show: WrappedShow,
    edit: WrappedEdit,
    create: WrappedCreate,
  };
};

// Lazy load all page modules
const JournalVoucher = lazyResource(() => import("pages/generalLedger/JournalVoucher"));
const AllocationVoucher = lazyResource(() => import("pages/generalLedger/AllocationVoucher"));
const StdVoucher = lazyResource(() => import("pages/generalLedger/StdVoucher"));
const RecurringVoucher = lazyResource(() => import("pages/generalLedger/RecurringVoucher"));
const AmortizationVoucher = lazyResource(() => import("pages/generalLedger/AmortizationVoucher"));
const AccountSummary = lazyResource(() => import("pages/generalLedger/AccountSummary"));
const FinancialReport = lazyResource(() => import("pages/generalLedger/FinancialReport"));
const ChartOfAccount = lazyResource(() => import("pages/generalLedger/ChartOfAccount"));
const Budget = lazyResource(() => import("pages/generalLedger/Budget"));
const GlProcedure = lazyResource(() => import("pages/generalLedger/Procedure"));

const ApVendor = lazyResource(() => import("pages/accountsPayable/ApVendor"));
const ApInvoice = lazyResource(() => import("pages/accountsPayable/ApInvoice"));
const ApPayment = lazyResource(() => import("pages/accountsPayable/ApPayment"));
const ApProcedure = lazyResource(() => import("pages/accountsPayable/Procedure"));

const ArProfile = lazyResource(() => import("pages/accountsReceivable/ArProfile"));
const ArFolio = lazyResource(() => import("pages/accountsReceivable/ArFolio"));
const ArInvoice = lazyResource(() => import("pages/accountsReceivable/ArInvoice"));
const ArReceipt = lazyResource(() => import("pages/accountsReceivable/ArReceipt"));
const ArProcedure = lazyResource(() => import("pages/accountsReceivable/Procedure"));

const AssetVendor = lazyResource(() => import("pages/asset/AssetVendor"));
const PreAsset = lazyResource(() => import("pages/asset/PreAsset"));
const AssetRegister = lazyResource(() => import("pages/asset/AssetRegister"));
const AssetDisposal = lazyResource(() => import("pages/asset/AssetDisposal"));
const AssetProcedure = lazyResource(() => import("pages/asset/Procedure"));

const Report = lazyResource(() => import("../pages/Report"));

const avartarWidth = {
  width: 30,
  height: 30,
  backgroundColor: "#2196f3",
};

const avatarText = {
  fontSize: 14,
};

const ProcedureIcon = () => {
  return <Icon>assignment</Icon>;
};

const ReportIcon = () => {
  return <Icon>assessment</Icon>;
};

const resources = [
  <Resource
    key="GL"
    name="menuGl"
    options={{ id: "GL", label: "General Ledger", IsSubmenu: true }}
    icon={
      <Avatar style={avartarWidth}>
        <span style={avatarText}>GL</span>
      </Avatar>
    }
  />,
  <Resource
    key="glJv"
    name="glJv"
    options={{
      label: "Journal Voucher",
      parentName: "menuGl",
      permitName: "GL.Jv",
      headerTitle: "Voucher",
      endHeader: true,
    }}
    icon={() => <Icon>fact_check</Icon>}
    {...JournalVoucher}
  />,
  <Resource
    key="glJvFr"
    name="glJvFr"
    options={{
      label: "Template Voucher",
      parentName: "menuGl",
      permitName: "GL.StdJv",
      headerTitle: "Template",
    }}
    icon={() => <Icon>today</Icon>}
    {...StdVoucher}
  />,
  <Resource
    key="recurringJv"
    name="recurringJv"
    options={{
      label: "Recurring Voucher",
      parentName: "menuGl",
      permitName: "GL.RecurringStdJv",
    }}
    icon={() => <IoCalendarSharp size={21} />}
    {...RecurringVoucher}
  />,
  <Resource
    key="amortizeJv"
    name="amortizeJv"
    options={{
      label: "Amortization Voucher",
      parentName: "menuGl",
      permitName: "GL.AmortizationStdJv",
    }}
    icon={() => <IoAlbumsSharp size={21} />}
    {...AmortizationVoucher}
  />,
  <Resource
    key="allocateJv"
    name="allocateJv"
    options={{
      label: "Allocation Voucher",
      parentName: "menuGl",
      permitName: "GL.AllocationJv",
      endHeader: true,
    }}
    icon={() => <HiDatabase size={21} />}
    {...AllocationVoucher}
  />,
  <Resource
    key="accountSummary"
    name="accountSummary"
    options={{ label: "Account Summary", parentName: "menuGl" }}
    icon={() => <Icon>addchart</Icon>}
    {...AccountSummary}
  />,
  <Resource
    key="financialReport"
    name="financialReport"
    options={{ label: "Financial Report", parentName: "menuGl", permitName: "GL.FinancialReport" }}
    icon={() => <Icon>article</Icon>}
    {...FinancialReport}
  />,
  <Resource
    key="chartOfAcc"
    name="accountCode"
    options={{
      label: "Chart of accounts",
      parentName: "menuGl",
      permitName: "GL.ChartOfAccount",
    }}
    icon={() => <Icon>view_list</Icon>}
    {...ChartOfAccount}
  />,
  <Resource
    key="budget"
    name="budget"
    options={{ label: "Budget", parentName: "menuGl", permitName: "GL.Budget" }}
    icon={() => <Icon>analytics</Icon>}
    {...Budget}
  />,
  <Resource
    key="menuProcedureGl"
    name="procedureGl"
    options={{ label: "Procedure", parentName: "menuGl" }}
    icon={ProcedureIcon}
    {...GlProcedure}
  />,
  <Resource
    key="reportGl"
    name="reportGl"
    options={{ label: "Report", parentName: "menuGl", groupName: "GL" }}
    icon={ReportIcon}
    {...Report}
  />,
  <Resource
    key="AP"
    name="menuAp"
    options={{ id: "AP", label: "Accounts Payable", IsSubmenu: true }}
    icon={
      <Avatar style={avartarWidth}>
        <span style={avatarText}>AP</span>
      </Avatar>
    }
  />,
  <Resource
    key="apVendor"
    name="apVendor"
    options={{ label: "Vendor", parentName: "menuAp", permitName: "AP.Vendor" }}
    icon={() => <Icon>supervised_user_circle</Icon>}
    {...ApVendor}
  />,
  <Resource
    key="apInvoice"
    name="apInvoice"
    options={{
      label: "Invoice",
      parentName: "menuAp",
      permitName: "AP.Invoice",
    }}
    icon={() => <Icon style={{ transform: "rotateY(180deg)" }}>sticky_note_2</Icon>}
    {...ApInvoice}
  />,
  <Resource
    key="apPayment"
    name="apPayment"
    options={{
      label: "Payment",
      parentName: "menuAp",
      permitName: "AP.Payment",
    }}
    icon={() => <Icon style={{ transform: "rotateY(180deg)" }}>view_sidebar</Icon>}
    {...ApPayment}
  />,
  <Resource
    key="menuProcedureAp"
    name="procedureAp"
    options={{ label: "Procedure", parentName: "menuAp" }}
    icon={ProcedureIcon}
    {...ApProcedure}
  />,
  <Resource
    key="reportAp"
    name="reportAp"
    options={{ label: "Report", parentName: "menuAp", groupName: "AP" }}
    icon={ReportIcon}
    {...Report}
  />,
  <Resource
    key="AR"
    name="menuAr"
    options={{ id: "AR", label: "Accounts Receivable", IsSubmenu: true }}
    icon={
      <Avatar style={avartarWidth}>
        <span style={avatarText}>AR</span>
      </Avatar>
    }
  />,
  <Resource
    key="arProfile"
    name="arProfile"
    options={{
      label: "A/R Profile",
      parentName: "menuAr",
      permitName: "AR.Profile",
    }}
    icon={() => <Icon>contact_page</Icon>}
    {...ArProfile}
  />,
  <Resource
    key="arFolio"
    name="arFolio"
    options={{ label: "Folio", parentName: "menuAr", permitName: "AR.Folio" }}
    icon={() => <Icon>shop_2</Icon>}
    {...ArFolio}
  />,
  <Resource
    key="arInvoice"
    name="arInvoice"
    options={{
      label: "Invoice",
      parentName: "menuAr",
      permitName: "AR.Invoice",
    }}
    icon={() => <Icon>shop</Icon>}
    {...ArInvoice}
  />,
  <Resource
    key="arReceipt"
    name="arReceipt"
    options={{
      label: "Receipt",
      parentName: "menuAr",
      permitName: "AR.Receipt",
    }}
    icon={() => <Icon>receipt</Icon>}
    {...ArReceipt}
  />,

  <Resource
    key="menuProcedureAr"
    name="procedureAr"
    options={{ label: "Procedure", parentName: "menuAr" }}
    icon={ProcedureIcon}
    {...ArProcedure}
  />,
  <Resource
    key="reportAr"
    name="reportAr"
    options={{ label: "Report", parentName: "menuAr", groupName: "AR" }}
    icon={ReportIcon}
    {...Report}
  />,
  <Resource
    key="AST"
    name="menuAsset"
    options={{ id: "AST", label: "Asset", IsSubmenu: true }}
    icon={
      <Avatar style={avartarWidth}>
        <span style={avatarText}>AS</span>
      </Avatar>
    }
  />,
  <Resource
    key="assetVendor"
    name="assetVendor"
    options={{
      label: "Vendor",
      parentName: "menuAsset",
      permitName: "Ast.Vendor",
    }}
    icon={() => <Icon>supervised_user_circle</Icon>}
    {...AssetVendor}
  />,
  <Resource
    key="preAsset"
    name="preAsset"
    options={{ label: "Pre-Register Asset", parentName: "menuAsset" }}
    icon={() => <Icon>web_asset</Icon>}
    {...PreAsset}
  />,
  <Resource
    key="assetRegister"
    name="assetRegister"
    options={{
      label: "Asset Register",
      parentName: "menuAsset",
      permitName: "Ast.Register",
    }}
    icon={() => <Icon>account_balance</Icon>}
    {...AssetRegister}
  />,
  <Resource
    key="assetDisposal"
    name="assetDisposal"
    options={{
      label: "Asset Disposal",
      parentName: "menuAsset",
      permitName: "Ast.Disposal",
    }}
    icon={() => <Icon>important_devices</Icon>}
    {...AssetDisposal}
  />,
  <Resource
    key="menuProcedureAsset"
    name="procedureAsset"
    options={{ label: "Procedure", parentName: "menuAsset" }}
    icon={ProcedureIcon}
    {...AssetProcedure}
  />,
  <Resource
    key="reportAsset"
    name="reportAsset"
    options={{ label: "Report", parentName: "menuAsset", groupName: "ASSET" }}
    icon={ReportIcon}
    {...Report}
  />,
];

export default resources;
